import type { PersonId } from './index.types';
import { StateStorage } from 'zustand/middleware';

export type { PersonId } from './index.types';

/**
 * Create a partitioned storage that prefixes keys with user ID.
 * This allows multiple users to have isolated data in the same browser.
 *
 * Note: This implementation uses localStorage (synchronous) instead of localforage
 * (async) because async storage operations can cause synchronization issues with
 * SIP and WebRTC when changing settings. In the future, this module will migrate
 * to use localforage exclusively for better scalability.
 *
 * @param personId - User ID for partitioning (required)
 * @returns StateStorage implementation with partitioned keys
 * @throws Error if personId is invalid
 */
export const createPartitionedStorage = (personId: PersonId): StateStorage => {
  if (!isValidPersonId(personId)) {
    throw new Error(
      '[softphone] Invalid personId provided to createPartitionedStorage'
    );
  }

  const prefix = `user-${personId}:`;

  return {
    getItem: (name: string) => {
      const value = localStorage.getItem(`${prefix}${name}`);
      return value;
    },
    setItem: (name: string, value: string) => {
      localStorage.setItem(`${prefix}${name}`, value);
    },
    removeItem: (name: string) => {
      localStorage.removeItem(`${prefix}${name}`);
    },
  };
};

/**
 * Type guard to validate person ID and clean invalid cache entries
 * @param personId - The person ID to validate
 * @param storeCache - Optional store cache to clean invalid entries
 * @returns True if the person ID is valid
 */
export const isValidPersonId = (
  personId: number | string | null | undefined,
  storeCache?: Map<string, unknown>
): personId is PersonId => {
  if (!personId) return false;
  if (personId === -1 || personId === '-1') return false;
  if (typeof personId === 'string' && personId.trim() === '') return false;

  // Clean invalid stores from cache if provided
  if (storeCache) {
    const invalidKeys = ['-1', 'user--1', 'undefined', 'null'];
    invalidKeys.forEach((key) => {
      if (storeCache.has(key)) {
        storeCache.delete(key);
      }
    });
  }

  return true;
};
